import { useEffect, useRef } from 'react';
import { useReduxState } from 'hooks';
import { localeNames } from 'shared-locales';
import {
  Checkbox,
  Form,
  FormGroup,
  Range,
  Select
} from 'components/common/form';
import { getCourseLanguageVersionOptions } from 'lib/courses';

import type { FormApi } from 'informed';
import type { CourseData, Locales } from 'shared-types';

interface Props {
  courseData: CourseData['data'];
  courseLanguage: CourseData['data']['language'];
  interfaceLanguage: Locales;
  updateCourseData: (data: Partial<CourseData['data']>) => void;
  updateCourseLanguage: (language: string) => void;
  updateInterfaceLanguage: (language: Locales) => void;
}

type FormValues = Pick<
  CourseData['data'],
  'completionAllowRetries' | 'completionTasks'
> & {
  courseLanguage: CourseData['data']['language'];
  interfaceLanguage: Locales;
};

const CourseDownloadScormCourseData = ({
  courseData,
  courseLanguage,
  interfaceLanguage,
  updateCourseData,
  updateCourseLanguage,
  updateInterfaceLanguage
}: Props) => {
  const {
    messages,
    serverConfig: { supportedLanguages }
  } = useReduxState();

  const formApi = useRef<FormApi>(undefined);

  const courseLanguages = getCourseLanguageVersionOptions({
    course: courseData
  });
  const hasLanguageVersions = Object.keys(courseLanguages).length > 1;

  // The interface language is recalculated when the course language changes.
  // Sync that state back into the form because informed ignores initialValues updates after mount.
  useEffect(() => {
    formApi.current?.setValue('interfaceLanguage', interfaceLanguage);
  }, [interfaceLanguage]);

  return (
    <div style={{ margin: '20px 0 40px 0' }}>
      <Form<FormValues>
        getApi={(form) => (formApi.current = form)}
        initialValues={{
          completionAllowRetries: courseData.completionAllowRetries,
          completionTasks: courseData.completionTasks,
          courseLanguage,
          interfaceLanguage
        }}
      >
        {() => (
          <>
            <FormGroup
              label={messages.settingsCompletion}
              labelHelpText={messages.manageExportScormRequirementsInfo}
            >
              <div className="fields">
                <Checkbox
                  field="completionAllowRetries"
                  id="completion-allow-retries"
                  label={messages.settingsCompletionAllowRetries}
                  onChange={(ev) =>
                    updateCourseData({
                      completionAllowRetries: (ev.target as HTMLInputElement)
                        .checked
                    })
                  }
                  style={{ marginBottom: 10 }}
                />

                <Range
                  field="completionTasks"
                  id="completion-tasks"
                  label={messages.settingsCompletionTasks}
                  max={100}
                  min={0}
                  onChange={(value) =>
                    updateCourseData({ completionTasks: value })
                  }
                  stepValue={10}
                  style={{ maxWidth: 320 }}
                  valueUnit="%"
                />
              </div>
            </FormGroup>

            {hasLanguageVersions && (
              <FormGroup label={messages.courseLanguage}>
                <Select
                  field="courseLanguage"
                  id="course-language"
                  onChange={updateCourseLanguage}
                  options={Object.keys(courseLanguages).map((language) => ({
                    label: courseLanguages[language].capitalizedName,
                    value: language
                  }))}
                  style={{ maxWidth: 200 }}
                />
              </FormGroup>
            )}

            <FormGroup
              label={messages.interfaceLanguage}
              labelHelpText={messages.manageExportScormLanguage}
            >
              <Select
                field="interfaceLanguage"
                id="interface-language"
                onChange={updateInterfaceLanguage}
                options={supportedLanguages.map((locale) => ({
                  label: localeNames[locale],
                  value: locale
                }))}
                style={{ maxWidth: 200 }}
              />
            </FormGroup>
          </>
        )}
      </Form>

      <style jsx>{`
        .fields {
          display: flex;
          flex-direction: column;
          gap: 5px;
          margin-top: 10px;
        }
      `}</style>
    </div>
  );
};

export default CourseDownloadScormCourseData;
